import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Elegantly using socket.io in backbone apps",
  "description": "",
  "published": "2012-12-14T00:00:00.000Z",
  "redirect_from": ["/blog/elegantly-using-socket-io-in-backbone-apps/swizec/5712"],
  "categories": "backbone, Client-side JavaScript, jQuery, socket.io, Uncategorized",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p><a parentName="p" {...{
        "href": "http://backbonejs.org/"
      }}>{`Backbone.js`}</a>{` is my favourite modern MVC framework for client-side javascript. Not that I've seriously tried any others ... simply didn't feel the need to.`}</p>
    <p>{`The way apps are usually organised is Backbone is that data is king. A view reacts to some user action and changes some data in a model. Everybody who's listening for that change then reacts and does something either to their view (adding something new on the screen, say) or data gets changed. Sometimes a change in data will cause the browser `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Uniform_Resource_Locator",
        "title": "Uniform Resource Locator"
      }}>{`URL`}</a>{` to change.`}</p>
    <p>{`Whatever, the important part is events are usually communicated through state changes.`}</p>
    <h2 {...{
      "id": "events"
    }}>{`Events`}</h2>
    <p>{`Despite the god-like status of models, most of your code goes into views. That's where you have to react to two types of events:`}</p>
    <ul>
      <li parentName="ul">{`data changes`}</li>
      <li parentName="ul">{`user actions`}</li>
    </ul>
    <p>{`Listening to data changes is easiest done in the `}<em parentName="p">{`initialize`}</em>{` function, something like so:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`    initialize: function () {
        this.model.on("change", this.render, this);
    }
`}</code></pre>
    <p>{`Okay, but what about user actions? Like somebody clicking on a button or entering a specific form field? Well, you could do it the old `}<a parentName="p" {...{
        "href": "http://jquery.com/",
        "title": "JQuery"
      }}>{`jQuery`}</a>{` way everyone's come to love by now.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`var that = this;
this.$el.find("a.button", function () {
  that.button_clicked();
});
`}</code></pre>
    <p>{`That can get unwieldy real quick ... Backbone has us covered with the events hash:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`    events: {
        "click a.button": "button_clicked"
    },
`}</code></pre>
    <p>{`Both of these approaches result in `}{`_`}{`this.button_clicked `}{`_`}{`being called when a user clicks the button. Except the second approach is much easier on the eye and significantly less convoluted when you have ten different events doing things.`}</p>
    <p>{`But what happens when you add socket.io to the mix?`}</p>
    <h2 {...{
      "id": "adding-socketio"
    }}>{`Adding socket.io`}</h2>
    <p>{`Backbone doesn't have any built-in support for a third source of events so you end up doing things the old way again. Adding listeners to a global socket in your `}<em parentName="p">{`initialize`}</em>{` function. Global sockets might sound like a bad idea, but I find having a single socket communicating with the server to make things easier.`}</p>
    <p>{`I usually keep the socket in the global `}{`_`}{`App `}{`_`}{`object.`}</p>
    <p>{`But this feels dirty:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`window.app.socket.on("message", function (message) {
  that.got_message(message);
});
`}</code></pre>
    <p>{`Or worse, handling everything about the message in the callback!`}</p>
    <p>{`Instead, I started making all my views extend a common `}<em parentName="p">{`MainView`}</em>{` that enables me to listen for socket events just like I would for user actions:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`var MyView = MainView.extend({

    socket_events: {
        "message": "got_message"
    }
`}</code></pre>
    <p>{`MainView then takes care of binding socket events to their callbacks much in the same way as Backbone's native View does.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`var MainView = Backbone.View.extend({
  initialize: function () {
    this.__initialize();
  },

  __initialize: function () {
    if (this.socket_events && _.size(this.socket_events) > 0) {
      this.delegateSocketEvents(this.socket_events);
    }
  },

  delegateSocketEvents: function (events) {
    for (var key in events) {
      var method = events[key];
      if (!_.isFunction(method)) {
        method = this[events[key]];
      }

      if (!method) {
        throw new Error('Method "' + events[key] + '" does not exist');
      }

      method = _.bind(method, this);
      window.app.socket.on(key, method);
    }
  },
});
`}</code></pre>
    <p>{`The reason there's two initialize functions is that we can't properly hook into the default View object and add some functionality. So we use initialize to do event delegation when the object is created - backbone calls initialize from the constructor - but if a child view needs their own initialize function, they should still be able to delegate socket events.`}</p>
    <p>{`Such a child view would simply call `}{`_`}{`this.`}{`_`}{`_`}{`initialize() `}{`_`}{`to execute MainView's initialize function and get things working.`}</p>
    <p>{`This is far from perfect and to get all of this really working the way I'd like - as seamlessly as user actions - I would have to make a pull request to Backbone ... but it's a good start to elegantly using socket.io in backbone apps.`}</p>
    <h6 {...{
      "id": "related-articles"
    }}>{`Related articles`}</h6>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://stackoverflow.com/questions/13391944/why-return-a-jquery-deferred-object-inside-backbone-sync"
        }}><img parentName="a" {...{
            "src": "https://i.zemanta.com/noimg_50_80_80.jpg",
            "alt": null
          }}></img></a><a parentName="li" {...{
          "href": "http://stackoverflow.com/questions/13391944/why-return-a-jquery-deferred-object-inside-backbone-sync"
        }}>{`why return a jquery deferred object inside backbone sync?`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://swizec.com/blog/testing-socket-io-apps/swizec/5625"
        }}><img parentName="a" {...{
            "src": "https://i.zemanta.com/129148469_80_80.jpg",
            "alt": null
          }}></img></a><a parentName="li" {...{
          "href": "http://swizec.com/blog/testing-socket-io-apps/swizec/5625"
        }}>{`Testing socket.io apps`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://backbonejs.org/?0-9-9"
        }}><img parentName="a" {...{
            "src": "https://i.zemanta.com/noimg_103_80_80.jpg",
            "alt": null
          }}></img></a><a parentName="li" {...{
          "href": "http://backbonejs.org/?0-9-9"
        }}>{`Backbone.js 0.9.9 Released`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://www.joezimjs.com/javascript/plugging-into-socket-io-the-basics/"
        }}><img parentName="a" {...{
            "src": "https://i.zemanta.com/125930383_80_80.jpg",
            "alt": null
          }}></img></a><a parentName="li" {...{
          "href": "http://www.joezimjs.com/javascript/plugging-into-socket-io-the-basics/"
        }}>{`Plugging Into Socket.IO: The Basics`}</a></li>
    </ul>
    <p><a parentName="p" {...{
        "href": "http://www.zemanta.com/?px",
        "title": "Enhanced by Zemanta"
      }}><img parentName="a" {...{
          "src": "https://img.zemanta.com/zemified_e.png?x-id=229aee28-470c-4b3b-b2bd-040326134ed5",
          "alt": "Enhanced by Zemanta"
        }}></img></a></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      